import { useState, useEffect, useMemo } from "react";
import { useLocation } from "wouter";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Search,
  Plus,
  Users,
  FileText,
  Settings,
  BarChart3,
  Calendar,
  Bell,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Command {
  id: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
  action: () => void;
  keywords?: string[];
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const [, setLocation] = useLocation();
  const [search, setSearch] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Define available commands
  const commands: Command[] = useMemo(
    () => [
      {
        id: "add-customer",
        label: "ເພີ່ມລູກຄ້າໃໝ່",
        description: "ສ້າງລູກຄ້າໃໝ່ໃນລະບົບ",
        icon: <Plus className="size-4" />,
        action: () => {
          setLocation("/add-customer");
          onOpenChange(false);
        },
        keywords: ["add", "new", "customer", "ເພີ່ມ", "ລູກຄ້າ"],
      },
      {
        id: "customers-list",
        label: "ລາຍການລູກຄ້າ",
        description: "ເບິ່ງລາຍການລູກຄ້າທັງໝົດ",
        icon: <Users className="size-4" />,
        action: () => {
          setLocation("/customers-list");
          onOpenChange(false);
        },
        keywords: ["customers", "list", "ລູກຄ້າ", "ລາຍການ"],
      },
      {
        id: "dashboard",
        label: "ໜ້າຫຼັກ",
        description: "ກັບໄປໜ້າ Dashboard",
        icon: <BarChart3 className="size-4" />,
        action: () => {
          setLocation("/dashboard");
          onOpenChange(false);
        },
        keywords: ["dashboard", "home", "ໜ້າຫຼັກ"],
      },
      {
        id: "reports",
        label: "ລາຍງານ",
        description: "ເບິ່ງລາຍງານຕ່າງໆ",
        icon: <FileText className="size-4" />,
        action: () => {
          setLocation("/reports");
          onOpenChange(false);
        },
        keywords: ["reports", "ລາຍງານ"],
      },
      {
        id: "users",
        label: "ຈັດການຜູ້ໃຊ້",
        description: "ຈັດການຜູ້ໃຊ້ລະບົບ",
        icon: <Users className="size-4" />,
        action: () => {
          setLocation("/users");
          onOpenChange(false);
        },
        keywords: ["users", "management", "ຜູ້ໃຊ້", "ຈັດການ"],
      },
      {
        id: "settings",
        label: "ຕັ້ງຄ່າລະບົບ",
        description: "ຕັ້ງຄ່າລະບົບທົ່ວໄປ",
        icon: <Settings className="size-4" />,
        action: () => {
          setLocation("/settings/system");
          onOpenChange(false);
        },
        keywords: ["settings", "ຕັ້ງຄ່າ"],
      },
      {
        id: "activity-log",
        label: "ບັນທຶກການກະທຳ",
        description: "ເບິ່ງປະຫວັດການກະທຳ",
        icon: <Calendar className="size-4" />,
        action: () => {
          setLocation("/activity-log");
          onOpenChange(false);
        },
        keywords: ["activity", "log", "history", "ບັນທຶກ", "ປະຫວັດ"],
      },
      {
        id: "notifications",
        label: "ການແຈ້ງເຕືອນ",
        description: "ເບິ່ງການແຈ້ງເຕືອນ",
        icon: <Bell className="size-4" />,
        action: () => {
          setLocation("/notifications");
          onOpenChange(false);
        },
        keywords: ["notifications", "alerts", "ແຈ້ງເຕືອນ"],
      },
    ],
    [setLocation, onOpenChange]
  );

  // Filter commands based on search
  const filteredCommands = useMemo(() => {
    if (!search.trim()) return commands;

    const searchLower = search.toLowerCase();
    return commands.filter((command) => {
      const labelMatch = command.label.toLowerCase().includes(searchLower);
      const descMatch = command.description?.toLowerCase().includes(searchLower);
      const keywordsMatch = command.keywords?.some((keyword) =>
        keyword.toLowerCase().includes(searchLower)
      );

      return labelMatch || descMatch || keywordsMatch;
    });
  }, [commands, search]);

  // Reset selected index when filtered commands change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredCommands]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < filteredCommands.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (filteredCommands[selectedIndex]) {
          filteredCommands[selectedIndex].action();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, filteredCommands, selectedIndex]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSearch("");
      setSelectedIndex(0);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0">
        <DialogHeader className="px-4 pt-4 pb-0">
          <DialogTitle className="sr-only">Command Palette</DialogTitle>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder="ຄົ້ນຫາຄຳສັ່ງ..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 border-0 focus-visible:ring-0 text-base"
              autoFocus
              aria-label="ຄົ້ນຫາຄຳສັງ"
              role="combobox"
              aria-expanded="true"
              aria-controls="command-list"
            />
          </div>
        </DialogHeader>

        <div className="max-h-[400px] overflow-y-auto p-2">
          {filteredCommands.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              ບໍ່ພົບຜົນການຄົ້ນຫາ
            </div>
          ) : (
            <div className="space-y-1" role="listbox" id="command-list" aria-label="ລາຍການຄຳສັງ">
              {filteredCommands.map((command, index) => (
                <button
                  key={command.id}
                  onClick={command.action}
                  onMouseEnter={() => setSelectedIndex(index)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left transition-colors",
                    "hover:bg-accent",
                    index === selectedIndex && "bg-accent"
                  )}
                  role="option"
                  aria-selected={index === selectedIndex}
                  aria-label={`${command.label}${command.description ? ` - ${command.description}` : ''}`}
                >
                  <div className="flex items-center justify-center size-8 rounded-md bg-muted">
                    {command.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{command.label}</div>
                    {command.description && (
                      <div className="text-xs text-muted-foreground truncate">
                        {command.description}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="border-t px-4 py-2 text-xs text-muted-foreground">
          <div className="flex items-center justify-between">
            <span>ໃຊ້ ↑↓ ເພື່ອເລືອກ, Enter ເພື່ອເປີດ</span>
            <span>Esc ເພື່ອປິດ</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
